import { useCallback, useRef } from 'react';
import { useFetcher } from 'react-router';

import { useBloomBasename } from '@jane/bloom/hooks/useBloomBasename';
import { useGooglePlaces } from '@jane/bloom/hooks/useGooglePlaces';
import type {
  DeliveryValidationResponse,
  Payload,
} from '@jane/bloom/routes/api.validate-delivery/types';
import { buildFormData } from '@jane/bloom/utils/formData';

interface ValidateDeliveryArgs {
  address: string;
}

export function useDeliveryValidation() {
  const basename = useBloomBasename();
  const fetcher = useFetcher<DeliveryValidationResponse>();
  const { getAddressDetails } = useGooglePlaces();

  const previousInputRef = useRef<string | null>(null);

  const validateDelivery = useCallback(
    async ({ address }: ValidateDeliveryArgs) => {
      const cacheKey = JSON.stringify({ address });

      if (previousInputRef.current === cacheKey) {
        return;
      }

      previousInputRef.current = cacheKey;

      const {
        coordinates: { lat, long },
        zipcode,
      } = await getAddressDetails(address);
      const formData: Payload = {
        lat,
        long,
        zip: zipcode,
      };
      fetcher.submit(buildFormData(formData), {
        action: `${basename}api/validate-delivery`,
        method: 'post',
      });
    },
    [basename, fetcher, getAddressDetails]
  );

  return {
    canDeliver:
      !fetcher.data /** fetcher errored or has not fetched yet */ ||
      fetcher.data.store_ids.length > 0,
    deliverableStoreIds: fetcher.data?.store_ids,
    hasValidated: !!fetcher.data,
    validateDelivery,
    validationState: fetcher.state,
  };
}
